export const IMAGE_PROFILES = Object.freeze({
  AVATAR: Object.freeze({ maxLongEdge: 1200, quality: 0.9, preserveTransparency: false }),
  LOGO: Object.freeze({ maxLongEdge: 1200, quality: 0.9, preserveTransparency: true }),
  BANNER: Object.freeze({ maxLongEdge: 2400, quality: 0.88, preserveTransparency: false }),
  PHOTO: Object.freeze({ maxLongEdge: 1920, quality: 0.88, preserveTransparency: false }),
});

export const MAX_SOURCE_IMAGE_BYTES = 40 * 1024 * 1024;
const RECOMPRESS_THRESHOLD_BYTES = 1024 * 1024;
const SUPPORTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

export const calculateResize = ({ width, height, maxLongEdge }) => {
  const longest = Math.max(Number(width || 0), Number(height || 0));
  if (!width || !height || longest <= maxLongEdge) return null;
  const scale = maxLongEdge / longest;
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
};

const decodeImage = async (file) => {
  if (typeof createImageBitmap === 'function') return createImageBitmap(file, { imageOrientation: 'from-image' });
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
    image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Unable to decode image.')); };
    image.src = url;
  });
};

const canvasBlob = (canvas, type, quality) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Unable to encode image.')), type, quality);
});

export const optimizeImageFile = async (file, profileName = 'PHOTO') => {
  if (!(file instanceof Blob)) throw new Error('Choose an image to continue.');
  if (file.size > MAX_SOURCE_IMAGE_BYTES) throw new Error('This image is too large to prepare safely. Please choose an image smaller than 40 MB.');
  const profile = IMAGE_PROFILES[profileName];
  if (!profile) throw new Error(`Unknown image optimization profile: ${profileName}`);
  if (file.type && !file.type.startsWith('image/')) throw new Error('Please choose a valid image.');

  let decoded;
  try { decoded = await decodeImage(file); } catch (_error) { throw new Error('Unable to prepare this image. Convert HEIC/HEIF to JPG and try again.'); }
  const width = decoded.width || decoded.naturalWidth;
  const height = decoded.height || decoded.naturalHeight;
  const resize = calculateResize({ width, height, maxLongEdge: profile.maxLongEdge });
  if (!resize && file.size <= RECOMPRESS_THRESHOLD_BYTES && SUPPORTED_TYPES.has(file.type)) {
    decoded.close?.();
    return file;
  }

  const outputType = profile.preserveTransparency && file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const target = resize || { width, height };
  const canvas = document.createElement('canvas');
  canvas.width = target.width;
  canvas.height = target.height;
  const context = canvas.getContext('2d', { alpha: outputType === 'image/png' });
  context.drawImage(decoded, 0, 0, target.width, target.height);
  decoded.close?.();
  let blob;
  try { blob = await canvasBlob(canvas, outputType, profile.quality); } catch (_error) { throw new Error('Unable to prepare image. Please try another image.'); }
  const extension = outputType === 'image/png' ? 'png' : 'jpg';
  return new File([blob], `${String(file.name || 'image').replace(/\.[^.]+$/, '')}.${extension}`, { type: outputType, lastModified: Date.now() });
};
